import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleBanner";

export function CampaignDetailsViewModel(logout, campaignId) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignProducts, setCampaignProducts] = useState([]);
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });

  const getCampaign = async function (id) {
    setLoading(true);
    try {
      const callResult = await makePostApiCall(`campaigns/${id}`, {});
      if (callResult.success) {
        setCampaign(callResult.data.campaign ?? callResult.data);
      } else if (callResult.errorStatus === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateCampaign = async function (id, values) {
    const payload = {
      name: values.name,
      location: values.location,
      start_date: values.start_date,
      end_date: values.end_date,
    };
    const callResult = await makePutApiCall(`campaigns/${id}`, payload);
    if (callResult.success) {
      setShowSimpleNotification({
        show: true,
        title: "Campaign updated",
        type: BannerNotificationType.Success,
      });
      getCampaign(id);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        setShowSimpleNotification({
          show: true,
          title: callResult.data?.message || "Could not update campaign",
          type: BannerNotificationType.Error,
        });
      }
    }
  };

  const getProductsForDropdown = async function (search, page) {
    const payload = { page: page || 1, search: search ?? null };
    const callResult = await makePostApiCall(`products`, payload);
    if (callResult.success) {
      return callResult.data;
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    return { data: [] };
  };

  const getCampaignProducts = async function (id) {
    if (!id) return;
    const callResult = await makePostApiCall("campaignProducts", {
      campaign_id: id,
    });
    if (callResult.success && callResult.data) {
      const raw = callResult.data;
      const data = Array.isArray(raw) ? raw : raw.data ?? [];
      setCampaignProducts(data);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const addCampaignProduct = async function (campaignId, product, quantity) {
    const qty = parseInt(quantity, 10) || 0;
    if (qty < 1 || !campaignId) return;
    const payload = {
      campaign_id: campaignId,
      product_id: product.id,
      quantity: qty,
    };
    const callResult = await makePostApiCall("addProductToCampaign", payload);
    if (callResult.success) {
      setShowSimpleNotification({
        show: true,
        title: "Product added to campaign",
        type: BannerNotificationType.Success,
      });
      getCampaignProducts(campaignId);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        setShowSimpleNotification({
          show: true,
          title: callResult.data?.message || "Could not add product to campaign",
          type: BannerNotificationType.Error,
        });
      }
    }
  };

  const removeCampaignProduct = async function (id) {
    const callResult = await makePostApiCall(`deleteCampaignProduct/${id}`, {});
    if (callResult.success) {
      setShowSimpleNotification({
        show: true,
        title: "Product removed from campaign",
        type: BannerNotificationType.Success,
      });
      getCampaignProducts(campaignId);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        setShowSimpleNotification({
          show: true,
          title: callResult.data?.message || "Could not remove product from campaign",
          type: BannerNotificationType.Error,
        });
      }
    }
  };

  return {
    campaign,
    campaignProducts,
    getCampaign,
    getCampaignProducts,
    getProductsForDropdown,
    addCampaignProduct,
    removeCampaignProduct,
    loading,
    setShowSimpleNotification,
    showSimpleNotification,
    updateCampaign,
  };
}
